import { PENDING_SEND_MAX_DELAY_SECONDS } from '@sunucupark/shared/lite';
import { create } from 'zustand';
import type { ComposerSeed, ComposerWindow, ComposerWindowState } from './types';

/**
 * The composer windows. UI state, not server state, so it lives in a store
 * rather than in the query cache (ADR 0006) — and in a module-level store
 * rather than in a component, so moving between folders, or to the settings
 * screen and back, never loses a half-written message.
 *
 * What is *in* each message is not here: that belongs to the window, which
 * autosaves it to the mail server.
 */

/**
 * How many composers stay open at once. A fourth one folds the oldest away
 * rather than pushing it off the screen; the docked stack is a row, and a row
 * of four does not fit next to a reading pane on a laptop.
 */
export const MAX_OPEN_COMPOSERS = 3;

export interface ComposerStore {
  /** Oldest first; the last one is nearest the corner. */
  readonly windows: readonly ComposerWindow[];
  /** The window that should take focus next, cleared once it has. */
  readonly focusRequest: string | null;
  open: (seed: ComposerSeed) => string;
  close: (id: string) => void;
  setWindowState: (id: string, state: ComposerWindowState) => void;
  setDraftId: (id: string, draftId: string) => void;
  /** Marks a window as taken over by its own route and removes it from the stack. */
  poppedOut: (id: string) => void;
  /** Told by a window once it has taken the focus it was asked to take. */
  focusHandled: (id: string) => void;
  closeAll: () => void;
  /**
   * How long a sent message waits before it really goes, in seconds; `0`
   * sends at once. The application owns the setting (it is a preference, and
   * one day an account's), the composer only obeys it.
   */
  readonly undoSendSeconds: number;
  setUndoSendSeconds: (seconds: number) => void;
}

/**
 * How long a message waits by default. Ten seconds is the length of an "oh
 * no" — long enough to take a message back, short enough that nobody watches
 * the tray wondering whether it left.
 */
export const DEFAULT_UNDO_SEND_SECONDS = 10;

/** A new window's id; `crypto.randomUUID` is available in every browser we support. */
const newId = (): string => `composer-${crypto.randomUUID()}`;

/** The window already showing this draft, so a second "open" focuses it. */
function existing(windows: readonly ComposerWindow[], seed: ComposerSeed): ComposerWindow | null {
  if (seed.draftId === undefined) return null;
  return windows.find((window) => window.draftId === seed.draftId) ?? null;
}

/** Folds the oldest open window away when a new one would make the row too long. */
function makeRoom(windows: readonly ComposerWindow[]): ComposerWindow[] {
  const open = windows.filter((window) => window.state !== 'minimised');
  if (open.length < MAX_OPEN_COMPOSERS) return [...windows];
  const oldest = open[0];
  return windows.map((window) =>
    window.id === oldest?.id ? { ...window, state: 'minimised' as const } : window,
  );
}

export const useComposerStore = create<ComposerStore>()((set, get) => ({
  windows: [],
  focusRequest: null,
  undoSendSeconds: DEFAULT_UNDO_SEND_SECONDS,

  setUndoSendSeconds: (seconds) => {
    set({ undoSendSeconds: Math.max(0, Math.min(Math.round(seconds), PENDING_SEND_MAX_DELAY_SECONDS)) });
  },

  open: (seed) => {
    const already = existing(get().windows, seed);
    if (already) {
      set((state) => ({
        windows: state.windows.map((window) =>
          window.id === already.id ? { ...window, state: 'normal' as const } : window,
        ),
        focusRequest: already.id,
      }));
      return already.id;
    }
    const id = newId();
    set((state) => ({
      windows: [
        ...makeRoom(state.windows),
        { id, seed, state: 'normal', draftId: seed.draftId ?? null },
      ],
      focusRequest: id,
    }));
    return id;
  },

  close: (id) =>
    set((state) => ({
      windows: state.windows.filter((window) => window.id !== id),
      focusRequest: state.focusRequest === id ? null : state.focusRequest,
    })),

  setWindowState: (id, windowState) =>
    set((state) => ({
      windows: state.windows.map((window) =>
        window.id === id ? { ...window, state: windowState } : window,
      ),
    })),

  setDraftId: (id, draftId) =>
    set((state) => ({
      windows: state.windows.map((window) => (window.id === id ? { ...window, draftId } : window)),
    })),

  poppedOut: (id) => {
    get().close(id);
  },

  focusHandled: (id) =>
    set((state) => ({ focusRequest: state.focusRequest === id ? null : state.focusRequest })),

  closeAll: () => {
    set({ windows: [], focusRequest: null });
  },
}));

/**
 * Opens a composer from anywhere — a toolbar button, a keyboard shortcut, the
 * reader's quick reply — without the caller holding a hook or knowing what a
 * composer window is.
 *
 * @example openComposer({ mode: 'replyAll', replyTo: message.id })
 */
export function openComposer(seed: ComposerSeed): string {
  return useComposerStore.getState().open(seed);
}

/**
 * How long a sent message may be taken back. Set by the application from the
 * person's settings; the composer never reads a preference store of its own.
 *
 * @example configureComposer({ undoSendSeconds: 10 })
 */
export function configureComposer(options: { undoSendSeconds: number }): void {
  useComposerStore.getState().setUndoSendSeconds(options.undoSendSeconds);
}

/** Closes every composer window; the drafts stay on the server. Used when the session ends. */
export function closeAllComposers(): void {
  useComposerStore.getState().closeAll();
}
